import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import { 
  getMidwayToken,
  type DecodedToken 
} from '../../utils/midway';

// For top Nav
interface MyTopNavigationProps {
  isDarkMode?: boolean;
  onToggleDarkMode?: () => void;
}

export function MyTopNavigation({ isDarkMode, onToggleDarkMode }: MyTopNavigationProps){
  const navigate = useNavigate();
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // All existing authentication logic preserved
  useEffect(() => {
    const handleAuthentication = async () => {
      try {
        console.log("Checking authentication status...");
        // Try to get a token (this will use mock token in local dev)
        try {
          const token = await getMidwayToken();
          setUser(token);
          setIsAuthenticated(true);
          console.log('Successfully authenticated user:', token.sub);
        } catch (error) {
          console.log('No valid token found, user needs to authenticate');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Authentication error:', error);
        setIsAuthenticated(false);
        setUser(null);
      }
    };
    handleAuthentication();
  }, []);

  const getUserDisplayName = () => {
    if (!user) return "Guest User";
    return user.sub || "Amazon User";
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={1} 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'var(--brand-primary) !important',
      }}
    >
      <Toolbar>
        {/* Logo and Title - Left Aligned */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="h5" 
            component="div" 
            onClick={() => navigate('/projects')}
            sx={{ 
              fontWeight: 600, 
              color: '#FFFFFF',
              fontSize: '1.1rem',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            DocScout - A Document AI Reviewer
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.75rem',
              fontWeight: 400
            }}
          >
            Built by DBA LegalTech
          </Typography>
        </Box>

        {/* Spacer to push right-side content to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Dark mode toggle */}
        {onToggleDarkMode && (
          <IconButton onClick={onToggleDarkMode} sx={{ color: '#FFFFFF' }}>
            {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        )}

        {/* Report a Bug Button */}
        <Button
          sx={{ color: '#FFFFFF' }}
          href="https://t.corp.amazon.com/create/templates/6f0894ff-48e3-43a4-b16f-c0c976ea4ed7"
          target="_blank"
          rel="noopener noreferrer"
        >
          Report a Bug
        </Button>

        {/* Wiki Button */}
        <Button
          sx={{ color: '#FFFFFF' }}
          href="https://w.amazon.com/bin/view/Legal/DBALegalTech/Products/DocScout/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Wiki
        </Button>

        {/* User Display */}
        {isAuthenticated && (
          <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountCircleIcon sx={{ color: '#FFFFFF' }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
              {getUserDisplayName()}
            </Typography>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
